import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { isAuthenticated, logout, getAuthToken, getTokenPayload } from './services/auth.service';
import { apiService } from './services/api.service';
import { startFleetHub, stopFleetHub } from './services/signalr.service';
import { disableWebPush, setupFleetWebPush } from './services/webPush.service';
import { startFleetNotificationRouter } from './services/fleetNotificationRouter';
import { initFleetLeaderLock, showFleetDesktopNotification } from './services/desktopNotify.service';
import { buildDeliveryDesktopNotification } from './utils/deliveryNotificationText';
import { FLEET_HUB_METHODS, API_ENDPOINTS, FLEET_APP_EVENTS, DispatchEventType } from './config/environment';
import { installFleetSoundUnlock, playFleetSound } from './utils/fleetSound';
import { routerOwnedType } from './utils/fleetPayloadOwnership';
import { showFleetToast, fleetToast } from './utils/toast';
import { canOpenPage, firstAccessiblePage } from './utils/fleetPermissions';
import LoginPage from './pages/auth/LoginPage';
import LegalAgreementGate from './components/LegalAgreementGate';
import './components/LegalAgreementGate.css';
import Sidebar from './components/layout/Sidebar';
import Header from './components/layout/Header';
import PwaInstallCard from './components/layout/PwaInstallCard';
import DashboardPage from './pages/dashboard/DashboardPage';
import MembersPage from './pages/members/MembersPage';
import DeliveriesPage from './pages/deliveries/DeliveriesPage';
import EarningsPage from './pages/earnings/EarningsPage';
import JoinRequestsPage from './pages/join-requests/JoinRequestsPage';
import ProfilePage from './pages/profile/ProfilePage';
import SupervisorAccountPage from './pages/profile/SupervisorAccountPage';
import ReportsPage from './pages/reports/ReportsPage';
import LiveMapPage from './pages/live-map/LiveMapPage';
import FleetHeatmapPage from './pages/heatmap/FleetHeatmapPage';
import CostHistoryPage from './pages/cost-history/CostHistoryPage';
import CourierCostsPage from './pages/courier-costs/CourierCostsPage';
import PayoutsPage from './pages/payouts/PayoutsPage';
import LegalAgreementsPage from './pages/legal-agreements/LegalAgreementsPage';
import DeliveryNotificationSlide from './components/DeliveryNotificationSlide';
import FleetReadinessPage from './pages/fleet-readiness/FleetReadinessPage';
import StackingFeePage from './pages/stacking-fee/StackingFeePage';
import PackageBonusPage from './pages/package-bonus/PackageBonusPage';
import SupervisorsPage from './pages/supervisors/SupervisorsPage';
import AnnouncementsPage from './pages/announcements/AnnouncementsPage';
import AnnouncementPopup from './components/common/AnnouncementPopup';
import FleetEmergencySection from './pages/emergency/FleetEmergencySection';
import FleetCriticalBar from './pages/emergency/FleetCriticalBar';
import { X, Loader2 } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './App.css';

const PAGE_TITLES = {
    dashboard: 'Anasayfa',
    members: 'Kuryelerim',
    deliveries: 'Teslimatlar',
    earnings: 'Gelir Paylaşımı',
    'cost-history': 'Filo Tarifesi',
    'courier-costs': 'Kurye Ücretleri',
    payouts: 'Hakedişler',
    'courier-payments': 'Kurye Hakedişleri',
    'workday-earnings': 'Mesai Hakedişleri',
    'fleet-payments': 'Filo Hakedişleri',
    'join-requests': 'Katılma Talepleri',
    invoices: 'Faturalar & Ödemeler',
    profile: 'Firma Profili',
    reports: 'Raporlar',
    'live-map': 'Canlı Harita',
    'heatmap': 'Isı Haritası',
    'legal-agreements': 'S\u00F6zle\u015Fmelerim',
    'stacking-fee': 'Yığın Kuralları',
    'fleet-readiness': 'Hazırlık Durumu',
    'package-bonus': 'Paket Bonusu',
    supervisors: 'Bölge Sorumluları',
};

/**
 * Hakediş ekranları tek sayfada birleşti. Eski bağlantılar (yer imi, bildirim yönlendirmesi,
 * paylaşılan URL) kırılmasın diye ilgili sekmeye çevrilir.
 */
const PAYOUT_TAB_OF_PAGE = {
    'fleet-payments': 'fleet',
    'workday-earnings': 'workday',
    'courier-payments': 'courier',
    invoices: 'invoices',
    'software-fee': 'software-fee',
};

/** /fleet-panel/me başarısız olursa aralarında bu kadar bekleyip en fazla 3 kez yeniden denenir
 * (1sn, 3sn, 6sn) — sonra kalıcı hata ekranı çizilir, sonsuza dek yarı-kilitli panelde kalınmaz. */
const ME_RETRY_DELAYS_MS = [1000, 3000, 6000];

/** #payouts:workday → 'payouts' (bilinmeyen hash anasayfaya düşer). */
function resolveHashPage(hash) {
    const raw = (hash || '').replace('#', '');
    const base = raw.split(':')[0];
    if (PAYOUT_TAB_OF_PAGE[base]) return 'payouts';
    return base && PAGE_TITLES[base] ? base : 'dashboard';
}

/** #payouts:workday → 'workday'; eski sayfa hash'i de kendi sekmesini açar. */
function resolveHashTab(hash) {
    const raw = (hash || '').replace('#', '');
    const [base, tab] = raw.split(':');
    if (PAYOUT_TAB_OF_PAGE[base]) return PAYOUT_TAB_OF_PAGE[base];
    return base === 'payouts' && tab ? tab : 'fleet';
}

/* Bildirim sesleri artık tek kaynakta (utils/fleetSound.js): tip başına ayrı desen,
   otomatik-oynatma kilidi, sessize alma ve spam koruması orada. Buradaki eski yerel
   Web Audio kopyası kaldırıldı — yalnız kritik paket sesi çalıyordu. */
const playCriticalSound = () => playFleetSound('critical');

/** Dispatch olayının ses tipi: yeni sipariş duyulur, atanamama/hata dikkat sesi verir. */
const dispatchSoundKind = (eventType) => {
    switch (Number(eventType)) {
        case DispatchEventType.OrderReceived:
            return 'newOrder';
        case DispatchEventType.CourierNotFound:
        case DispatchEventType.AssignmentExpired:
        case DispatchEventType.RegionMatchFailed:
        case DispatchEventType.InsufficientBalance:
        case DispatchEventType.PartnerNotReady:
        case DispatchEventType.FleetNotReady:
        case DispatchEventType.CourierNotReady:
        case DispatchEventType.CourierRejected:
            return 'critical';
        case DispatchEventType.OrderDelivered:
        case DispatchEventType.CourierAccepted:
            return 'success';
        default:
            return 'info';
    }
};

function App() {
    const [authenticated, setAuthenticated] = useState(() => {
        const params = new URLSearchParams(window.location.search);
        const t = params.get('t');
        const r = params.get('r');
        if (t && r) {
            localStorage.setItem('fleet-access-token', t);
            localStorage.setItem('fleet-refresh-token', r);
            window.history.replaceState({}, '', window.location.pathname);
            return true;
        }
        return isAuthenticated();
    });
    const [agreementsChecked, setAgreementsChecked] = useState(false);
    // Sayfa = URL hash (#deliveries) → F5 sonrası aynı sayfada kal (router lib yok, sunucu configi gerekmez).
    // Hakedişler sekmeli olduğu için hash ikinci bir parça taşıyabilir: #payouts:workday
    const [activePage, setActivePage] = useState(() => resolveHashPage(window.location.hash));
    const [payoutTab, setPayoutTab] = useState(() => resolveHashTab(window.location.hash));
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [fleetName, setFleetName] = useState(null);
    /** Filonun kendi logosu (data URL) — yoksa Hızlıyo görseli gösterilir. */
    const [fleetLogo, setFleetLogo] = useState(null);
    const [activeToastCount, setActiveToastCount] = useState(0);
    /* Sol menü rozeti AÇIK paketi sayar: bekleyen (kuryesiz) + aktif (kuryede). Yalnız
       bekleyeni saymak, kuryeye verilmiş ama hâlâ yoldaki paketleri görünmez kılıyordu. */
    const [deliveryCounts, setDeliveryCounts] = useState({ pending: 0, active: 0 });
    const [criticalSummary, setCriticalSummary] = useState(null);
    const prevCriticalRef = React.useRef(0);
    const [deliveryNotifications, setDeliveryNotifications] = useState([]);
    const [fleetNotifications, setFleetNotifications] = useState([]);
    const [fleetId, setFleetId] = useState(null);
    // Yetki bağlamı: { isOwner, permissions, areas, fleetId, fleetName, displayName } — bkz. utils/fleetPermissions.
    const [accessCtx, setAccessCtx] = useState(null);
    // 3 deneme de başarısız olursa true — renderPage içerik yerine tam sayfa hata ekranı çizer.
    const [accessError, setAccessError] = useState(false);
    // "Tekrar dene" düğmesi bunu artırır; efekt bağımlılığında olduğu için yeniden çalışır.
    const [meRetryTick, setMeRetryTick] = useState(0);

    const loadNotificationsFromDb = useCallback(async (fId) => {
        if (!fId) return;
        try {
            const res = await apiService.get(API_ENDPOINTS.NOTIFICATIONS.LIST(fId));
            // Zarf tek katman (apiService açıyor): liste doğrudan `res.data`.
            // `res.data.success` aranırken koşul hiç tutmuyor, kayıtlı bildirimler
            // panelde hiç görünmüyordu.
            if (res.success && Array.isArray(res.data)) {
                const dbNotifs = res.data.map(n => ({
                    id: n.id,
                    type: n.dataJson ? JSON.parse(n.dataJson)?.type : 'General',
                    title: n.title,
                    message: n.body,
                    time: n.sentAt || n.createdAtUtc,
                    read: !!n.readAt,
                    fromDb: true,
                }));
                setFleetNotifications(prev => {
                    const memoryOnly = prev.filter(p => !p.fromDb);
                    const merged = [...memoryOnly, ...dbNotifs];
                    const unique = merged.filter((v, i, a) => a.findIndex(t => t.id === v.id) === i);
                    return unique.sort((a, b) => new Date(b.time) - new Date(a.time)).slice(0, 50);
                });
            }
        } catch {}
    }, []);

    const handleMarkNotificationRead = useCallback(async (notifId) => {
        setFleetNotifications(prev => prev.map(n => n.id === notifId ? { ...n, read: true } : n));
        try {
            await apiService.put(API_ENDPOINTS.NOTIFICATIONS.MARK_READ(notifId));
        } catch {}
    }, []);

    const extractUserInfo = useCallback(() => {
        const token = getAuthToken();
        if (!token) return null;
        const payload = getTokenPayload(token);
        if (!payload) return null;
        const name = payload.name || payload.unique_name || payload.sub || 'Filo';
        const email = payload.email || payload['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] || '';
        return { name, email };
    }, []);

    useEffect(() => {
        const counts = { current: 0 };
        const unsubscribe = toast.onChange((payload) => {
            if (payload.status === 'added') counts.current++;
            else if (payload.status === 'removed') counts.current = Math.max(0, counts.current - 1);
            setActiveToastCount(counts.current);
        });
        return () => { if (typeof unsubscribe === 'function') unsubscribe(); };
    }, []);

    const fetchPendingDeliveryCount = useCallback(async () => {
        try {
            const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_TAB_COUNTS);
            if (response.success && response.data) {
                setDeliveryCounts({
                    pending: response.data.pending || 0,
                    active: response.data.active || 0,
                });
            }
        } catch {}
    }, []);

    // Kritik paket özeti (atanmamış + gecikmiş) — global bar + sidebar rozeti + ses.
    const fetchCriticalSummary = useCallback(async () => {
        try {
            const response = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_CRITICAL_SUMMARY);
            if (response.success && response.data) {
                const total = response.data.totalCritical || 0;
                if (total > prevCriticalRef.current) playCriticalSound();
                prevCriticalRef.current = total;
                setCriticalSummary(response.data);
            }
        } catch {}
    }, []);

    /* Sayaçlar OLAY-TETİKLEMELİ tazelenir; bu tur güvenlik ağıdır.
       Paket sayısı tek bir açılış çağrısıyla okunuyordu: bağlantı koptuğu, sekme uykuya
       daldığı ya da olay tipini kimse sahiplenmediği anda rozet o anki sayıda donuyor ve
       yeni paket sol menüde hiç görünmüyordu. Kritik özet zaten 30 sn'de bir dönüyordu;
       paket sayacı da AYNI tura bindirildi — ikinci bir zamanlayıcı yok. */
    useEffect(() => {
        if (!authenticated) return;

        const refreshAll = () => {
            fetchPendingDeliveryCount();
            fetchCriticalSummary();
        };

        refreshAll();
        const id = setInterval(refreshAll, 30000);

        /* Sekme arkaplandayken tarayıcı zamanlayıcıyı kısıyor; kullanıcı geri döndüğünde
           rozet dakikalarca eski kalabiliyordu. Öne gelir gelmez taze sayı okunur. */
        const onVisible = () => { if (document.visibilityState === 'visible') refreshAll(); };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('focus', onVisible);

        /* Bu paneldeki kendi işlemimiz (kurye atama/transfer) karşı taraftan push üretmez;
           katılma talebi rozetinde olduğu gibi uygulama-içi olayla tazelenir. */
        window.addEventListener(FLEET_APP_EVENTS.DELIVERIES_CHANGED, refreshAll);

        return () => {
            clearInterval(id);
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('focus', onVisible);
            window.removeEventListener(FLEET_APP_EVENTS.DELIVERIES_CHANGED, refreshAll);
        };
    }, [authenticated, fetchPendingDeliveryCount, fetchCriticalSummary]);

    /* Profil bir kez okunur; logo değişince (ProfilePage) FLEET_PROFILE_CHANGED ile tazelenir —
       sol menü ve kullanıcı rozeti aynı veriyi gösterdiği için sayfa yenilemeye gerek kalmaz. */
    const loadFleetProfile = useCallback(async () => {
        try {
            const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.PROFILE);
            if (!res.success || !res.data) return;

            if (res.data.name) setFleetName(res.data.name);
            setFleetLogo(res.data.logoBase64
                ? `data:${res.data.logoContentType || 'image/png'};base64,${res.data.logoBase64}`
                : null);

            if (res.data.id) {
                setFleetId(res.data.id);
                loadNotificationsFromDb(res.data.id);
            }
        } catch { /* yut */ }
    }, [loadNotificationsFromDb]);

    useEffect(() => {
        if (authenticated) {
            setUserInfo(extractUserInfo());
            loadFleetProfile();
        }
    }, [authenticated, extractUserInfo, loadFleetProfile]);

    useEffect(() => {
        if (!authenticated) return;
        const handler = () => loadFleetProfile();
        window.addEventListener(FLEET_APP_EVENTS.FLEET_PROFILE_CHANGED, handler);
        return () => window.removeEventListener(FLEET_APP_EVENTS.FLEET_PROFILE_CHANGED, handler);
    }, [authenticated, loadFleetProfile]);

    // Yetki bağlamı: menüler ve sayfa erişimi buna göre çizilir. Sahipte maske 0 gelse de
    // isOwner true olduğu için tüm kapılar açıktır (bkz. utils/fleetPermissions).
    //
    // apiService ağ hatasında/403'te throw ETMEZ — handleError her durumda {success:false}
    // döndürür (bkz. services/api.service.js). Bu yüzden başarısızlık burada try/catch değil,
    // res.success kontrolüyle yakalanır; 3 deneme de başarısız olursa accessError'a düşer.
    useEffect(() => {
        if (!authenticated) { setAccessCtx(null); setAccessError(false); return; }

        let cancelled = false;
        const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

        (async () => {
            setAccessError(false);

            // attempt 0 ilk deneme, sonraki ME_RETRY_DELAYS_MS.length denemesi artan gecikmeyle
            // yeniden dener (1sn, 3sn, 6sn) — toplam en fazla 1 + 3 çağrı.
            for (let attempt = 0; attempt <= ME_RETRY_DELAYS_MS.length; attempt++) {
                if (attempt > 0) {
                    await wait(ME_RETRY_DELAYS_MS[attempt - 1]);
                    if (cancelled) return;
                }

                const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.ME);
                if (cancelled) return;

                if (res?.success && res.data) {
                    setAccessCtx(res.data);
                    return;
                }
            }

            setAccessError(true);
            fleetToast.error('Panel yetkileriniz yüklenemedi. Bağlantınızı kontrol edip tekrar deneyin.', { toastId: 'fleet-me-error' });
        })();

        return () => { cancelled = true; };
    }, [authenticated, meRetryTick]);

    /* Giriş sonrası varsayılan sayfa anasayfadır, ama anasayfa artık yetkiye bağlı.
       Yetkisi olmayan sorumluyu açılışta "erişiminiz yok" ekranıyla karşılamamak için
       bağlam gelir gelmez erişebildiği ilk sayfaya taşınır. Adres çubuğunda hash varsa
       (kullanıcı bilerek bir sayfaya gitmiş) karışılmaz. */
    useEffect(() => {
        if (!accessCtx || window.location.hash) return;
        if (canOpenPage(activePage, accessCtx)) return;

        const target = firstAccessiblePage(accessCtx);
        if (target !== activePage) handleNavigate(target);
        // Yalnız bağlam geldiğinde çalışır: activePage'i bağımlılığa koymak, kullanıcı
        // sonradan yetkisiz bir sayfaya giderse onu geri fırlatır (ret ekranı görünmez olur).
    }, [accessCtx]); // eslint-disable-line

    useEffect(() => {
        if (authenticated) {
            startFleetHub().catch(() => {});
            /* Kilit kimliği JWT'den okunur, fleetId'den DEĞİL: fleetId'yi asenkron profil
               isteği dolduruyor ve bu efekt ilk çalıştığında henüz null. initFleetLeaderLock
               tek atımlık olduğu için o anda 'anon'a kilitlenir ve gerçek id'yi bir daha
               almaz. AuthUserId localStorage'daki token'dan senkron gelir ve backend'in
               fleet:{authUserId} SignalR grubuyla aynı granülerlikte. */
            const tokenPayload = getTokenPayload();
            const authUserId = tokenPayload?.sub || tokenPayload?.nameid || null;
            initFleetLeaderLock(authUserId);
            setupFleetWebPush().catch(() => {});

            const stopNotificationRouter = startFleetNotificationRouter({
                onNotification: ({ type, title, body, raw }) => {
                    setFleetNotifications(prev => [
                        { id: Date.now(), type, title, message: body, time: new Date(), read: false, ...raw },
                        ...prev,
                    ].slice(0, 50));
                },
            });

            // Ses/toast/bildirim kararı artık fleetNotificationRouter'da; bu olayın App.jsx
            // tarafında tazelenecek verisi yok, yalnız dinleyici kaydı korunuyor.
            const handleJoinRequest = () => {};

            /* Bu iki kanalın (teslimat durumu + eski dispatch DTO'su) TEK çıkış noktası.
               Kayan kart ile masaüstü bildirimi aynı olaydan, aynı metinle üretilir;
               kart çıkıp bildirim çıkmaması (ekranın başında olmayan filo yöneticisi için
               olayın tümüyle kaybolması) böyle engellenir.

               force: backend bu olaylar için filoya FCM push ATMAZ — SendToFleetAsync yalnız
               kritik dispatch/hakediş/katılma tiplerinde çağrılıyor. Web push token'ı olan
               sekmede sayfa çizimi bastırılsaydı (çift bildirim kuralı) kullanıcı hiçbir
               masaüstü bildirimi görmezdi; burada çizecek ikinci bir kanal yok. */
            const emitDeliveryNotification = (notification) => {
                setDeliveryNotifications(prev => [...prev.slice(-4), notification]);
                showFleetDesktopNotification({
                    ...buildDeliveryDesktopNotification(notification),
                    force: true,
                });
                fetchPendingDeliveryCount();
                fetchCriticalSummary();
            };

            const handleDeliveryUpdate = (e) => {
                const d = e.detail || {};
                /* TEK TÜKETİCİ KURALI: bu kanal iki gövde taşıyor. `type` alanı olan gövdeleri
                   (bugün yalnız DelayReminder) fleetNotificationRouter sahiplenir — başlık,
                   gövde, ses, toast ve masaüstü bildirimi oradan çıkar. Burada da işlenseydi
                   kayan kart ACTION_CONFIG'te karşılığı olmadığı için genel "Teslimat"
                   kartına düşer ve kullanıcı aynı olayı iki kez, biri boş görürdü. */
                if (routerOwnedType(d)) return;

                const notification = {
                    id: Date.now() + Math.random(),
                    action: d.action || d.Action || 'StatusChanged',
                    courierName: d.courierName || d.CourierName || null,
                    externalOrderId: d.externalOrderId || d.ExternalOrderId || null,
                    internalOrderNumber: d.internalOrderNumber || d.InternalOrderNumber || null,
                    newStatus: d.newStatus || d.NewStatus || null,
                    previousStatus: d.previousStatus || d.PreviousStatus || null,
                    deliveryId: d.deliveryId || d.DeliveryId || null,
                    partnerName: d.partnerName || d.PartnerName || null,
                };
                /* Teslimat durum değişimi: teslim edildi olumlu, gerisi genel bildirim sesi. */
                playFleetSound(String(notification.newStatus) === '7' ? 'success' : 'info');
                emitDeliveryNotification(notification);
            };

            const handleDispatchNotification = (e) => {
                const d = e.detail || {};
                /* TEK TÜKETİCİ KURALI: bu kanal iki gövde taşıyor. Task 5'in {type,title,body,...}
                   biçimini fleetNotificationRouter sahiplenir; burada da işlenirse DeliveryNotificationSlide
                   `eventType` bulamayıp DEFAULT_CONFIG'e düşer ve anlamlı kartın yanına ikinci,
                   boş bir "Teslimat" kartı çizilirdi (CourierNotFound ve MaxReassign'da ikisi
                   birden geliyor). Burada YALNIZ eski AdminDispatchNotificationConsumer DTO'su işlenir. */
                if (routerOwnedType(d)) return;

                const notification = {
                    id: Date.now() + Math.random(),
                    eventType: d.EventType || d.eventType,
                    message: d.Message || d.message || null,
                    deliveryId: d.DeliveryId || d.deliveryId || null,
                    orderId: d.OrderId || d.orderId || null,
                    externalOrderId: d.ExternalOrderId || d.externalOrderId || null,
                    internalOrderNumber: d.InternalOrderNumber || d.internalOrderNumber || null,
                    partnerName: d.PartnerName || d.partnerName || null,
                    regionName: d.RegionName || d.regionName || null,
                    courierName: d.CourierName || d.courierName || null,
                };
                /* Ses BURADA çalar: eski DTO `eventType` (enum) taşır, `type` taşımaz — router
                   `if (!type) return` ile bu gövdeleri atlıyor. Ses router'a devredilmiş sayılırsa
                   CourierAssigned, CourierAccepted, CourierRejected, ReassignmentStarted,
                   AssignmentExpired ve eski CourierNotFound tümüyle sessiz kalır. */
                playFleetSound(dispatchSoundKind(notification.eventType));
                emitDeliveryNotification(notification);
            };

            const handleMemberUpdate = (e) => {
                playFleetSound('info');
                showFleetToast({
                    icon: 'users', color: '#10b981',
                    title: 'Üye Güncellemesi',
                    message: 'Filo üye bilgileri güncellendi',
                    autoClose: 5000,
                });
            };

            const handleCourierLocation = () => {};

            /* Ses/toast/masaüstü kararı ve bildirim listesine ekleme fleetNotificationRouter'da
               (onNotification → setFleetNotifications). Buradaki tek iş SAYAÇ.
               Router'ın sahiplendiği tipler (atanamamış paket, gecikme hatırlatması) tam da yeni
               paketin filoya düştüğü anlar; bu dinleyici boş kaldığı sürece o paketler sol menü
               rozetine hiç yansımıyordu — kullanıcının "siparişler kaçıyor" dediği durum. */
            const handleFleetNotification = () => {
                fetchPendingDeliveryCount();
                fetchCriticalSummary();
            };

            window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_NOTIFICATION}`, handleFleetNotification);
            window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`, handleJoinRequest);
            window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handleDeliveryUpdate);
            window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_DISPATCH_NOTIFICATION}`, handleDispatchNotification);
            window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_MEMBER_UPDATE_NOTIFICATION}`, handleMemberUpdate);
            window.addEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_COURIER_LOCATION_UPDATE}`, handleCourierLocation);

            return () => {
                window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_NOTIFICATION}`, handleFleetNotification);
                window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`, handleJoinRequest);
                window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_DELIVERY_UPDATE_NOTIFICATION}`, handleDeliveryUpdate);
                window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_FLEET_DISPATCH_NOTIFICATION}`, handleDispatchNotification);
                window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_MEMBER_UPDATE_NOTIFICATION}`, handleMemberUpdate);
                window.removeEventListener(`fleet:${FLEET_HUB_METHODS.RECEIVE_COURIER_LOCATION_UPDATE}`, handleCourierLocation);
                stopNotificationRouter();
                stopFleetHub();
            };
        }
    }, [authenticated, fetchPendingDeliveryCount, fetchCriticalSummary]);

    useEffect(() => {
        const handleLogout = () => {
            stopFleetHub();
            setAuthenticated(false);
            setAgreementsChecked(false);
            setUserInfo(null);
            setFleetName(null);
            setActivePage('dashboard');
            if (window.location.hash) window.history.replaceState({}, '', window.location.pathname);
        };

        const handleTokenRefreshed = () => {
            setUserInfo(extractUserInfo());
        };

        window.addEventListener('auth:logout', handleLogout);
        window.addEventListener('auth:token_refreshed', handleTokenRefreshed);

        return () => {
            window.removeEventListener('auth:logout', handleLogout);
            window.removeEventListener('auth:token_refreshed', handleTokenRefreshed);
        };
    }, [extractUserInfo]);

    const handleLoginSuccess = () => {
        setAuthenticated(true);
        setAgreementsChecked(false);
        setUserInfo(extractUserInfo());
        showFleetToast({ icon: 'check', color: '#22c55e', title: 'Hoş Geldiniz', message: 'Giriş başarılı', autoClose: 2000 });
    };

    const handleLogout = async () => {
        stopFleetHub();
        /* Önce token'ı düşür: logout() clearTokens() çağırıyor, ondan sonra
           unregister-device isteği Authorization başlığı bulamaz ve 401 alır. */
        await disableWebPush();
        logout();
        setAuthenticated(false);
        setAgreementsChecked(false);
        setUserInfo(null);
        setFleetName(null);
        setActivePage('dashboard');
        if (window.location.hash) window.history.replaceState({}, '', window.location.pathname);
    };

    const handleNavigate = (page) => {
        // Eski hakediş sayfaları artık Hakedişler ekranının sekmesi.
        const tab = PAYOUT_TAB_OF_PAGE[page];
        const target = tab ? 'payouts' : page;
        if (tab) setPayoutTab(tab);

        setActivePage(target);

        const hash = tab ? `payouts:${tab}` : target;
        if (window.location.hash.replace('#', '') !== hash) {
            window.location.hash = hash;
        }
        if (window.innerWidth < 768) {
            setSidebarOpen(false);
        }
    };

    /* Arka plan push bildirimine tıklandığında yönlendirme.

       fleet-sw.js'in notificationclick işleyicisi açık bir pencere bulursa onu focus() eder ve
       {type:'hizliyo-fleet-notification-click', link} mesajını yollar. Bu dinleyici olmadan
       sekme öne gelir ama SAYFA DEĞİŞMEZ: derin bağlantı yalnız hiç pencere açık değilken
       (clients.openWindow) çalışırdı. Partner panelindeki app-sw.js / SignalRProvider
       eşleşmesinin filo karşılığı; buradaki fark rotanın hash olması. */
    useEffect(() => {
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

        const onSwMessage = (event) => {
            const data = (event && event.data) || {};
            if (data.type !== 'hizliyo-fleet-notification-click') return;

            const hash = String(data.link || '').split('#')[1];
            if (!hash) return;

            try { window.focus(); } catch { /* yut */ }

            /* Hash zaten aynıysa hashchange tetiklenmez; durumu doğrudan da kurarız. */
            setActivePage(resolveHashPage(`#${hash}`));
            setPayoutTab(resolveHashTab(`#${hash}`));
            if (window.location.hash.replace('#', '') !== hash) {
                window.location.hash = hash;
            }
        };

        try { navigator.serviceWorker.addEventListener('message', onSwMessage); } catch { /* yut */ }
        return () => {
            try { navigator.serviceWorker.removeEventListener('message', onSwMessage); } catch { /* yut */ }
        };
    }, []);

    // Geri/İleri ve manuel hash değişimini senkronla.
    useEffect(() => {
        const onHash = () => {
            const hash = window.location.hash;
            if (!hash) return;
            setActivePage(resolveHashPage(hash));
            setPayoutTab(resolveHashTab(hash));
        };
        window.addEventListener('hashchange', onHash);
        return () => window.removeEventListener('hashchange', onHash);
    }, []);

    if (!authenticated) {
        return (
            <>
                <LoginPage onLoginSuccess={handleLoginSuccess} />
                <ToastContainer position="top-right" autoClose={4000} newestOnTop closeOnClick draggable={false} pauseOnHover={false} pauseOnFocusLoss={false} theme="light" limit={3} hideProgressBar={false} className="ft-toast-container" />
            </>
        );
    }

    if (!agreementsChecked) {
        return (
            <>
                <LegalAgreementGate onComplete={() => setAgreementsChecked(true)} />
                <ToastContainer position="top-right" autoClose={4000} newestOnTop closeOnClick draggable={false} pauseOnHover={false} pauseOnFocusLoss={false} theme="light" limit={3} hideProgressBar={false} className="ft-toast-container" />
            </>
        );
    }

    const renderPage = () => {
        // 3 deneme de başarısız → yarı-kilitli (menüsüz ama içerikli) ekran yerine tam sayfa
        // hata durumu. Yetki kapısından ÖNCE döner: accessCtx yokken canOpenPage zaten false
        // dönüyor olsa da, kullanıcıya "erişimin yok" değil "yüklenemedi" mesajı gösterilmeli.
        if (accessError) {
            return (
                <div className="fl-denied">
                    <h2>Yetki bilgisi alınamadı</h2>
                    <p>Panel yetkileriniz yüklenemedi. Bağlantınızı kontrol edip tekrar deneyin.</p>
                    <button className="fl-denied-btn" onClick={() => setMeRetryTick(t => t + 1)}>Tekrar dene</button>
                </div>
            );
        }

        // Bağlam henüz gelmedi ve hata da yok (ilk yükleme): Sidebar menüsüz duruyor, içerik de
        // boş/yanlış bir sayfa göstermesin diye kısa bir yükleniyor göstergesi çizilir.
        if (!accessCtx) {
            return (
                <div className="page-loading"><Loader2 className="spinning" size={40} /><p>Yükleniyor...</p></div>
            );
        }

        // Sahipte accessCtx.isOwner true olduğu için canOpenPage her zaman true döner —
        // bu kapı sahibi hiç durdurmaz, yalnız sorumlunun yetkisiz sayfa denemesini keser.
        if (!canOpenPage(activePage, accessCtx)) {
            return (
                <div className="fl-denied">
                    <h2>Bu sayfaya erişiminiz yok</h2>
                    <p>Bu bölüm filo yöneticisi tarafından size açılmamış.</p>
                    {/* Anasayfa da yetkiye bağlı: körlemesine oraya göndermek, yetkisi
                        olmayan sorumluyu bu ekranda kilitliyordu. */}
                    <button className="fl-denied-btn" onClick={() => handleNavigate(firstAccessiblePage(accessCtx))}>
                        Erişebildiğim sayfaya git
                    </button>
                </div>
            );
        }

        switch (activePage) {
            case 'dashboard':
                /* Parasal bloklar yalnız sahibe: sorumlu panoda kendi kapsamının
                   operasyonel istatistiklerini görür, filonun ticari rakamlarını değil. */
                return <DashboardPage onNavigate={handleNavigate} showFinancials={!!accessCtx?.isOwner} />;
            case 'members':
                return <MembersPage />;
            case 'deliveries':
                return <DeliveriesPage />;
            case 'earnings':
                return <EarningsPage />;
            case 'cost-history':
                return <CostHistoryPage />;
            case 'courier-costs':
                return <CourierCostsPage />;
            case 'stacking-fee':
                return <StackingFeePage />;
            // Hakediş ekranlarının hepsi tek sayfada; eski id'ler handleNavigate/resolveHashPage
            // tarafından zaten 'payouts'a çevriliyor.
            case 'payouts':
                return <PayoutsPage initialTab={payoutTab} />;
            case 'join-requests':
                return <JoinRequestsPage />;
            case 'profile':
                /* Sorumlu, filo profilini (vergi no, firma iletişimi, logo yönetimi, hesap silme)
                   GÖRMEZ — o veri filo sahibinindir. Sorumluya kendi hesap ekranı çizilir. */
                return accessCtx && !accessCtx.isOwner
                    ? <SupervisorAccountPage ctx={accessCtx} />
                    : <ProfilePage />;
            case 'announcements':
                return <AnnouncementsPage />;
            case 'reports':
                return <ReportsPage />;
            case 'live-map':
                return <LiveMapPage accessCtx={accessCtx} />;
            case 'heatmap':
                return <FleetHeatmapPage />;
            case 'legal-agreements':
                return <LegalAgreementsPage />;
            case 'fleet-readiness':
                return <FleetReadinessPage />;
            case 'package-bonus':
                return <PackageBonusPage />;
            case 'supervisors':
                return <SupervisorsPage />;
            default:
                return <DashboardPage />;
        }
    };

    return (
        <div className="fleet-app">
            <Sidebar
                activePage={activePage}
                onNavigate={handleNavigate}
                userInfo={userInfo}
                fleetName={fleetName}
                fleetLogo={fleetLogo}
                onLogout={handleLogout}
                isOpen={sidebarOpen}
                onToggle={() => setSidebarOpen(!sidebarOpen)}
                openDeliveryCount={deliveryCounts.pending + deliveryCounts.active}
                criticalCount={criticalSummary?.totalCritical || 0}
                accessCtx={accessCtx}
            />

            {sidebarOpen && (
                <div className="fleet-overlay" onClick={() => setSidebarOpen(false)} />
            )}

            <div className="fleet-main">
                <Header
                    /* Sorumluda #profile firma profilini değil kendi hesabını gösterir. */
                    title={activePage === 'profile' && accessCtx && !accessCtx.isOwner
                        ? 'Hesabım'
                        : (PAGE_TITLES[activePage] || 'Anasayfa')}
                    onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
                    userInfo={userInfo}
                    fleetName={fleetName}
                    fleetLogo={fleetLogo}
                    onNavigate={handleNavigate}
                    notifications={fleetNotifications}
                    onClearNotifications={() => setFleetNotifications([])}
                    onMarkNotificationRead={handleMarkNotificationRead}
                    accessCtx={accessCtx}
                />
                <div className="fleet-content">
                    <FleetEmergencySection />
                    <FleetCriticalBar summary={criticalSummary} onNavigate={handleNavigate} />
                    {renderPage()}
                </div>
            </div>

            {/* Açılış duyurusu: oturum başına bir kez sorulur, yönlendirme yapmaz. */}
            <AnnouncementPopup onNavigate={handleNavigate} />

            {/* "Uygulama olarak kur" daveti — yalnız oturum açıkken ve kurulu değilken çıkar. */}
            <PwaInstallCard />

            <DeliveryNotificationSlide
                notifications={deliveryNotifications}
                onDismiss={(id) => setDeliveryNotifications(prev => prev.filter(n => n.id !== id))}
                onDismissAll={() => { setDeliveryNotifications([]); toast.dismiss(); setActiveToastCount(0); }}
            />

            {deliveryNotifications.length === 0 && activeToastCount >= 2 && createPortal(
                <button className="ft-toast-dismiss-all" onClick={() => { toast.dismiss(); setActiveToastCount(0); }}>
                    <X size={14} />
                    Tümünü Kapat ({activeToastCount})
                </button>,
                document.body
            )}

            <ToastContainer
                position="top-right"
                autoClose={4000}
                hideProgressBar={false}
                newestOnTop
                closeOnClick
                draggable={false}
                pauseOnHover={false}
                pauseOnFocusLoss={false}
                theme="light"
                limit={3}
                className="ft-toast-container"
            />
        </div>
    );
}

export default App;
